import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, CalendarDays, Receipt, ScrollText, LogOut, Stethoscope, UsersRound, Settings } from "lucide-react";
import { useAuth } from "@/auth";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  { to: "/patients", label: "Patients", icon: Users, tid: "nav-patients" },
  { to: "/scheduler", label: "Scheduler", icon: CalendarDays, tid: "nav-scheduler" },
  { to: "/billing", label: "Billing", icon: Receipt, tid: "nav-billing" },
  { to: "/users", label: "Staff", icon: UsersRound, tid: "nav-users", ownerOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings", ownerOnly: true },
  { to: "/audit", label: "Audit Log", icon: ScrollText, tid: "nav-audit", ownerOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen bg-[#FDFDFB]">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-[#0A3326] text-[#F3F4F6] flex flex-col" data-testid="sidebar">
        <div className="px-5 py-6 flex items-center gap-2 border-b border-white/10">
          <div className="w-8 h-8 rounded-md bg-[#E27B58] flex items-center justify-center">
            <Stethoscope className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg tracking-tight leading-none">DentOS</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">Practice Suite</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {nav.filter((n) => !n.ownerOnly || user?.role === "OWNER").map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#134D3B] text-white"
                    : "text-white/70 hover:bg-[#134D3B]/60 hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={2} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <div className="px-2 py-2">
            <div className="text-xs text-white/50">Signed in as</div>
            <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-[#E27B58]/90 mt-0.5">{user?.role}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-white/80 hover:bg-[#134D3B] hover:text-white"
          >
            <LogOut className="w-4 h-4" strokeWidth={2} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
